/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Tomáš Burian (burian@cadwork.cz)
 *
 */

import FileAnimationControls from "App/view/controls/file_animation_controls";
import CameraWebVR from "App/view/cameras/camera_web_vr";
import {mat4, vec3} from "gl-matrix";
import ViewModeWebVR from "App/view/modes/view_mode_web_vr";
import RenderDocument from "App/render_document";
import View from "App/view/view";
import {EViewControls} from "js_interface/src/modules/view_controls/scene_view_controls_enums";

export default
class FileAnimationControlsWebVR extends FileAnimationControls {
    constructor(aDocument: RenderDocument, aView: View) {
        super(aDocument, aView);

        // replace the orthographic camera created by the WalkControls->ViewControls
        this._camera = new CameraWebVR(this._view);
        this._initCameraParams();
    }

    public getControlsType(): EViewControls {
        return EViewControls.FILE_ANIMATION_WEBVR;
    }

    public render() {
        let cameraCurrentPositionWithPose = this._camera.getCameraPosition();

        let frameData = (this._viewMode as ViewModeWebVR).getFrameData();

        if (!frameData) {
            return;
        }

        let orientMat = mat4.clone(frameData.leftViewMatrix as mat4);
        // we are not interested in the offset of the eye but the orientation
        orientMat[12] = 0.0;
        orientMat[13] = 0.0;
        orientMat[14] = 0.0;
        orientMat[15] = 1.0;

        // rotate target based on the eye matrix around eye, we need inverse rotation
        let yAxis = vec3.create();
        vec3.transformMat4(yAxis, vec3.fromValues(0, 1, 0), orientMat);
        let initRotationMat = mat4.create();
        mat4.rotate(initRotationMat, initRotationMat, /*this._cumulYRot*/0.0, vec3.fromValues(0, 1, 0));

        let rotationMat = mat4.create();
        mat4.multiply(rotationMat, orientMat, initRotationMat);

        let inverseRotationMat = mat4.create();
        mat4.invert(inverseRotationMat, rotationMat);

        // we need to rotate initial view vector and also the up vector
        let dir = vec3.create();
        let upVector = vec3.create();
        vec3.transformMat4(dir, vec3.fromValues(0, 0, -1), inverseRotationMat);
        vec3.transformMat4(upVector, vec3.fromValues(0, 1, 0), inverseRotationMat);

        // normalize the direction amd up vectors
        vec3.normalize(dir, dir);
        vec3.normalize(upVector, upVector);

        // compute new target based on the new view direction computed from the eye rotation
        let newTarget = vec3.create();
        vec3.add(newTarget, cameraCurrentPositionWithPose, dir);
        // now we need to apply the pose transformation to both eye and target

        let inverseInitYMat = mat4.create();
        mat4.invert(inverseInitYMat, initRotationMat);

        let yrotpose = vec3.create();
        let oldPose = (this._camera as CameraWebVR).getPose();
        let newPose = vec3.create();
        if (frameData.pose.position) {
            newPose = frameData.pose.position as vec3;
        }
        vec3.add(yrotpose, vec3.fromValues(-oldPose[0], -oldPose[1], -oldPose[2]), newPose);
        vec3.transformMat4(yrotpose, yrotpose, inverseInitYMat);

        let newEye = vec3.create();
        vec3.add(newEye, cameraCurrentPositionWithPose, yrotpose);
        vec3.add(newTarget, newTarget, yrotpose);

        // now we can compute the model view matrix
        (this._camera as CameraWebVR).setPose(newPose);
        (this._camera as CameraWebVR).setInverseRotationmatrix(inverseRotationMat);
        this._camera.setCameraPosition(newEye);
        this._camera.setCameraTarget(newTarget);
        this._camera.setCameraUpVector(upVector);
    }
}
